import { useRef, useState } from 'react';
import { useNotification } from '@/hooks';
import { getDateRangeErrors } from '@/components/inputs/dateRangeValidation';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { getErrorMessage } from '@/utils/errorUtils';
import {
  useCreateTrackingIconMutation,
  useUpdateTrackingIconMutation,
  useReplaceTrackingIconImageMutation,
  useDisableTrackingIconMutation,
  useDeleteTrackingIconMutation,
  useLazyGetTrackingIconByIdQuery,
  useLazyGetTrackingIconAvailabilityQuery,
} from '../services/tracking-icons.service';
import { TrackingIcon, TrackingIconSlot } from '../models';
import {
  TrackingIconFormValues,
  trackingIconPayload,
  trackingIconDefaultValues,
} from '../models/TrackingIconForm';
import {
  trackingIconBlockedRanges,
  trackingIconEnabledDefault,
} from '../utils/trackingIconAvailability';

const useTrackingIconActions = () => {
  const [createTrackingIcon, { isLoading: isCreatingTrackingIcon }] =
    useCreateTrackingIconMutation();
  const [updateTrackingIcon, { isLoading: isUpdatingTrackingIcon }] =
    useUpdateTrackingIconMutation();
  const [
    replaceTrackingIconImage,
    { isLoading: isReplacingTrackingIconImage },
  ] = useReplaceTrackingIconImageMutation();
  const [disableTrackingIcon, { isLoading: isDisablingTrackingIcon }] =
    useDisableTrackingIconMutation();
  const [deleteTrackingIcon, { isLoading: isDeletingTrackingIcon }] =
    useDeleteTrackingIconMutation();
  const [getTrackingIcon] = useLazyGetTrackingIconByIdQuery();
  const [getAvailability] = useLazyGetTrackingIconAvailabilityQuery();
  const [isEnablingTrackingIcon, setIsEnablingTrackingIcon] = useState(false);
  const enabling = useRef(false);
  const deleting = useRef(false);

  const { showNotification } = useNotification();

  const onCreateTrackingIcon = async (
    slot: TrackingIconSlot,
    data: TrackingIconFormValues,
    onSuccess: () => void
  ) => {
    try {
      const body = new FormData();
      body.append('slot', slot);
      Object.entries(trackingIconPayload(data)).forEach(([key, value]) => {
        if (value !== null) body.append(key, String(value));
      });
      body.append('image', data.image!);

      await createTrackingIcon(body).unwrap();
      showNotification(
        'Ícono creado correctamente',
        NotificationSeverity.success
      );
      onSuccess();
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        'Ocurrió un error al crear el ícono. Inténtalo nuevamente.'
      );
      showNotification(errorMessage, NotificationSeverity.error);
      return errorMessage;
    }
  };

  const onUpdateTrackingIcon = async (
    id: string,
    data: TrackingIconFormValues,
    onSuccess: () => void
  ) => {
    try {
      await updateTrackingIcon({
        id,
        data: trackingIconPayload(data),
      }).unwrap();
      showNotification(
        'Ícono actualizado correctamente',
        NotificationSeverity.success
      );
      onSuccess();
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        'Ocurrió un error al actualizar el ícono. Inténtalo nuevamente.'
      );
      showNotification(errorMessage, NotificationSeverity.error);
      return errorMessage;
    }
  };

  const onReplaceTrackingIconImage = async (
    id: string,
    data: TrackingIconFormValues,
    onSuccess: () => void
  ) => {
    try {
      const body = new FormData();
      body.append('image', data.image!);

      await replaceTrackingIconImage({ id, data: body }).unwrap();
      showNotification(
        'Imagen reemplazada correctamente',
        NotificationSeverity.success
      );
      onSuccess();
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        'Ocurrió un error al reemplazar la imagen. Inténtalo nuevamente.'
      );
      showNotification(errorMessage, NotificationSeverity.error);
      return errorMessage;
    }
  };

  const onEnableTrackingIcon = async (id: string) => {
    if (enabling.current) return;
    if (!window.confirm('¿Habilitar este ícono?')) return;

    enabling.current = true;
    setIsEnablingTrackingIcon(true);
    try {
      // Read current values before using the existing update contract, so
      // re-enabling does not overwrite edits made since this view was opened.
      const icon = await getTrackingIcon(id, false).unwrap();
      if (icon.enabled) {
        showNotification(
          'El ícono ya está habilitado',
          NotificationSeverity.info
        );
        return;
      }
      const data = { ...trackingIconDefaultValues(icon), enabled: true };
      try {
        const icons = await getAvailability(icon.slot, false).unwrap();
        if (data.isDefault) {
          const existingDefault = trackingIconEnabledDefault(
            icons,
            icon.slot,
            icon.id
          );
          if (existingDefault) {
            return 'Ya existe un ícono predeterminado habilitado para este tipo. Deshabilitalo antes de habilitar otro.';
          }
        } else {
          const occupied = trackingIconBlockedRanges(icons, icon.slot, icon.id);
          const errors = getDateRangeErrors(
            { from: data.dateFrom, to: data.dateTo },
            { withTime: true, allowOpenRange: true, blockedRanges: occupied }
          );
          if (errors.from || errors.to) {
            return `${errors.from || errors.to} Usá «Editar datos» para ajustar la vigencia antes de habilitar el ícono.`;
          }
        }
      } catch {
        return 'No pudimos comprobar la disponibilidad. Reintentá antes de habilitar el ícono.';
      }
      await updateTrackingIcon({
        id,
        data: trackingIconPayload(data),
      }).unwrap();
      showNotification(
        'Ícono habilitado correctamente',
        NotificationSeverity.success
      );
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        'Ocurrió un error al habilitar el ícono. Inténtalo nuevamente.'
      );
      showNotification(errorMessage, NotificationSeverity.error);
      return errorMessage;
    } finally {
      enabling.current = false;
      setIsEnablingTrackingIcon(false);
    }
  };

  const onDisableTrackingIcon = async (id: string, onSuccess: () => void) => {
    if (!window.confirm('¿Deshabilitar este ícono?')) return;

    try {
      await disableTrackingIcon(id).unwrap();
      showNotification(
        'Ícono deshabilitado correctamente',
        NotificationSeverity.success
      );
      onSuccess();
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        'Ocurrió un error al deshabilitar el ícono. Inténtalo nuevamente.'
      );
      showNotification(errorMessage, NotificationSeverity.error);
    }
  };

  const onDeleteTrackingIcon = async (
    icon: TrackingIcon,
    onSuccess: () => void
  ): Promise<void> => {
    if (deleting.current) return;

    const confirmed = window.confirm(
      `¿Eliminar definitivamente el ícono "${icon.name}"?\n\n` +
        'También se eliminará su programación, si la tiene. ' +
        'Esta acción no se puede deshacer.'
    );

    if (!confirmed) return;

    deleting.current = true;

    try {
      await deleteTrackingIcon(icon.id).unwrap();
      showNotification(
        'Ícono eliminado correctamente',
        NotificationSeverity.success
      );
      onSuccess();
    } catch (error: unknown) {
      const message = getErrorMessage(
        error,
        'No se pudo eliminar el ícono. Inténtalo nuevamente.'
      );
      showNotification(message, NotificationSeverity.error);
    } finally {
      deleting.current = false;
    }
  };

  return {
    onCreateTrackingIcon,
    onUpdateTrackingIcon,
    onReplaceTrackingIconImage,
    onDisableTrackingIcon,
    onDeleteTrackingIcon,
    onEnableTrackingIcon,
    isEnablingTrackingIcon,
    isCreatingTrackingIcon,
    isUpdatingTrackingIcon,
    isReplacingTrackingIconImage,
    isDisablingTrackingIcon,
    isDeletingTrackingIcon,
    isSavingTrackingIcon:
      isCreatingTrackingIcon ||
      isUpdatingTrackingIcon ||
      isReplacingTrackingIconImage ||
      isDisablingTrackingIcon ||
      isEnablingTrackingIcon ||
      isDeletingTrackingIcon,
  };
};

export default useTrackingIconActions;
